/**
 * Phone number utilities for international phone handling
 * Supports formatting, validation, and country code detection
 */

import { COUNTRIES, CountryInfo } from '../constants/countries';

export interface ParsedPhoneNumber {
  countryCode: string;
  nationalNumber: string;
  formattedNumber: string;
  e164: string;
  isValid: boolean;
}

/**
 * Common country calling codes
 */
export const CALLING_CODES: Record<string, string> = {
  '+1': 'US', // Also Canada, Caribbean
  '+52': 'MX',
  '+55': 'BR',
  '+58': 'VE',
  '+57': 'CO',
  '+509': 'HT',
  '+44': 'GB',
  '+33': 'FR',
  '+49': 'DE',
  '+34': 'ES',
  '+351': 'PT',
  '+39': 'IT',
  '+86': 'CN',
  '+81': 'JP',
  '+82': 'KR',
  '+84': 'VN',
  '+7': 'RU',
  '+91': 'IN',
  '+63': 'PH',
  '+20': 'EG',
  '+234': 'NG',
};

/**
 * Country code to calling code mapping
 */
export const COUNTRY_TO_CALLING_CODE: Record<string, string> = Object.entries(CALLING_CODES)
  .reduce((acc, [calling, country]) => {
    acc[country] = calling;
    return acc;
  }, {} as Record<string, string>);

/**
 * Clean phone number by removing non-digit characters
 */
export const cleanPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  // Keep only digits and leading +
  return phone.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');
};

/**
 * Extract country code from phone number
 */
export const extractCountryCode = (phone: string): { countryCode: string | null; remaining: string } => {
  const cleaned = cleanPhoneNumber(phone);
  
  if (!cleaned.startsWith('+')) {
    return { countryCode: null, remaining: cleaned };
  }

  // Try to match calling codes (longest first)
  const sortedCodes = Object.keys(CALLING_CODES).sort((a, b) => b.length - a.length);
  
  for (const code of sortedCodes) {
    if (cleaned.startsWith(code)) {
      return {
        countryCode: code,
        remaining: cleaned.slice(code.length),
      };
    }
  }

  return { countryCode: null, remaining: cleaned };
};

/**
 * Format phone number for display based on country
 */
export const formatPhoneNumber = (phone: string, countryCode?: string): string => {
  const cleaned = cleanPhoneNumber(phone);
  
  if (!cleaned) return '';

  // If it's a US/Canada number (+1)
  if (countryCode === 'US' || countryCode === 'CA' || cleaned.startsWith('+1') || cleaned.startsWith('1')) {
    const digits = cleaned.replace(/^\+?1/, '');
    if (digits.length === 10) {
      return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
  }

  // If it's a Mexican number (+52)
  if (countryCode === 'MX' || cleaned.startsWith('+52')) {
    const digits = cleaned.replace(/^\+?52/, '');
    if (digits.length === 10) {
      return `+52 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    }
  }

  // If it's a Brazilian number (+55)
  if (countryCode === 'BR' || cleaned.startsWith('+55')) {
    const digits = cleaned.replace(/^\+?55/, '');
    if (digits.length === 10 || digits.length === 11) {
      const area = digits.slice(0, 2);
      const rest = digits.slice(2);
      if (rest.length === 9) {
        return `+55 ${area} ${rest.slice(0, 5)}-${rest.slice(5)}`;
      } else {
        return `+55 ${area} ${rest.slice(0, 4)}-${rest.slice(4)}`;
      }
    }
  }

  // Generic international format
  if (cleaned.startsWith('+')) {
    const { countryCode: code, remaining } = extractCountryCode(cleaned);
    if (code) {
      return `${code} ${remaining}`;
    }
  }

  // Default: just return cleaned
  return cleaned;
};

/**
 * Convert phone number to E.164 format
 */
export const toE164 = (phone: string, defaultCountryCode?: string): string => {
  const cleaned = cleanPhoneNumber(phone);
  
  if (!cleaned) return '';

  // Already in E.164 format
  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  // Add country code if provided
  if (defaultCountryCode) {
    const callingCode = COUNTRY_TO_CALLING_CODE[defaultCountryCode];
    if (callingCode) {
      // Handle US numbers that might start with 1
      if (defaultCountryCode === 'US' && cleaned.startsWith('1') && cleaned.length === 11) {
        return `+${cleaned}`;
      }
      return `${callingCode}${cleaned}`;
    }
  }

  // Assume US if 10 digits without country code
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }

  // Assume US if 11 digits starting with 1
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`;
  }

  return cleaned;
};

/**
 * Parse a phone number into its components
 */
export const parsePhoneNumber = (phone: string, defaultCountryCode?: string): ParsedPhoneNumber => {
  const e164 = toE164(phone, defaultCountryCode);
  const { countryCode, remaining } = extractCountryCode(e164);
  
  return {
    countryCode: countryCode || '',
    nationalNumber: remaining,
    formattedNumber: formatPhoneNumber(e164),
    e164,
    isValid: validatePhoneNumberBasic(e164),
  };
};

/**
 * Basic phone number validation
 */
export const validatePhoneNumberBasic = (phone: string): boolean => {
  const cleaned = cleanPhoneNumber(phone);
  
  if (!cleaned) return false;

  // Must have at least 7 digits
  const digitsOnly = cleaned.replace(/\D/g, '');
  if (digitsOnly.length < 7) return false;

  // Must not exceed E.164 max length (15 digits)
  if (digitsOnly.length > 15) return false;

  return true;
};

/**
 * Detect if a phone number is likely to have WhatsApp
 * Based on country and phone type heuristics
 */
export const isWhatsAppLikely = (phone: string): boolean => {
  const parsed = parsePhoneNumber(phone);
  
  if (!parsed.isValid) return false;

  // Countries with high WhatsApp adoption
  const highWhatsAppCountries = ['BR', 'MX', 'VE', 'CO', 'ES', 'IT', 'DE', 'IN'];
  
  const countryFromCode = parsed.countryCode ? CALLING_CODES[parsed.countryCode] : null;
  
  if (countryFromCode && highWhatsAppCountries.includes(countryFromCode)) {
    return true;
  }

  // For US/CA, WhatsApp is less common but still possible
  // Return true for international-looking numbers
  if (parsed.countryCode === '+1') {
    return false; // Default to SMS for US numbers
  }

  // For other international numbers, assume WhatsApp is available
  return parsed.countryCode !== null && parsed.countryCode !== '+1';
};

/**
 * Get country info from phone number
 */
export const getCountryFromPhone = (phone: string): CountryInfo | null => {
  const parsed = parsePhoneNumber(phone);
  
  if (!parsed.countryCode) return null;

  const countryCode = CALLING_CODES[parsed.countryCode];
  
  if (!countryCode) return null;

  return COUNTRIES[countryCode] || null;
};

/**
 * Format phone number for display in a specific locale
 */
export const formatPhoneForLocale = (phone: string, locale: string): string => {
  const parsed = parsePhoneNumber(phone);
  
  if (!parsed.isValid) return phone;

  // For Arabic locale, keep digits but may need RTL handling
  if (locale === 'ar') {
    // Use LTR embedding for phone numbers in RTL context
    return `\u202A${parsed.formattedNumber}\u202C`;
  }

  return parsed.formattedNumber;
};

/**
 * Mask phone number for privacy (show only last 4 digits)
 */
export const maskPhoneNumber = (phone: string): string => {
  const parsed = parsePhoneNumber(phone);
  
  if (!parsed.isValid || parsed.nationalNumber.length < 4) {
    return '****';
  }

  const lastFour = parsed.nationalNumber.slice(-4);
  const maskedPart = '*'.repeat(parsed.nationalNumber.length - 4);
  
  return `${parsed.countryCode} ${maskedPart}${lastFour}`;
};

/**
 * Check if two phone numbers are equivalent
 */
export const phoneNumbersEqual = (phone1: string, phone2: string): boolean => {
  const e164_1 = toE164(phone1);
  const e164_2 = toE164(phone2);
  
  return e164_1 === e164_2;
};

/**
 * Generate a phone input mask based on country
 */
export const getPhoneMask = (countryCode: string): string => {
  const masks: Record<string, string> = {
    US: '(999) 999-9999',
    CA: '(999) 999-9999',
    MX: '999 999 9999',
    BR: '(99) 99999-9999',
    VE: '(999) 999-9999',
    CO: '(999) 999-9999',
    HT: '9999 9999',
    GB: '99999 999999',
    FR: '99 99 99 99 99',
    DE: '9999 9999999',
    ES: '999 999 999',
    PT: '999 999 999',
  };

  return masks[countryCode] || '999 999 999 999'; // Generic international
};

/**
 * Get placeholder text for phone input based on country
 */
export const getPhonePlaceholder = (countryCode: string): string => {
  const placeholders: Record<string, string> = {
    US: '(555) 123-4567',
    CA: '(555) 123-4567',
    MX: '555 123 4567',
    BR: '(11) 91234-5678',
    VE: '(412) 123-4567',
    CO: '(301) 234-5678',
    HT: '2234 5678',
    GB: '07911 123456',
    FR: '06 12 34 56 78',
    DE: '0151 12345678',
    ES: '612 345 678',
  };

  return placeholders[countryCode] || '123 456 789';
};